import Admin from "../Admin";
import React from "react";
import CardAdminPermissionGeneral from "../../../Component/Card/Admin/Permission/CardAdminPermissionGeneral";
import CardAdminPermissionRoles from "../../../Component/Card/Admin/Permission/CardAdminPermissionRoles";
import PageHeader from "../../../Component/PageHeader";
import MUIButton from "@mui/material/Button";
import MUIDeleteIcon from "@mui/icons-material/Delete";
import MUISaveButton from "@mui/icons-material/Save";
import MUILoadingButton from "@mui/lab/LoadingButton";
import Menu from "../../../Component/Menu";
import DialogConfirm from "../../../Component/Dialog/DialogConfirm";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import api from "../../../Seating/Security/api";
import Validator from "../../../Utility/Validator";
import Permission from "../../../Utility/Crud/Permission";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";

export default class AdminPermissionCreateEdit extends Admin {
  constructor(props) {
    super(props);

    this.state.permission = null;
    this.state.isProcessing = false;
    this.state.isUpdate = !!props.match.params.id;
    this.state.confirmDialogOpen = false;
    this.state.validationIssues = {};
    this.state.selectedRoleIds = [];
    this.state.originalRolePermissionRows = [];
  }

  afterComponentDidMount() {
    const permissionId = this.props.match.params.id
      ? +this.props.match.params.id
      : null;
    if (permissionId !== null) {
      const rolePermissionRows =
        this.getRolePermissionRowsForPermission(permissionId);
      this.setState({
        permission: this.context.permissionsIndexed[permissionId],
        selectedRoleIds: rolePermissionRows.map(
          (rolePermission) => rolePermission.roleId,
        ),
        originalRolePermissionRows: JSON.parse(
          JSON.stringify(rolePermissionRows),
        ),
      });
    }
  }

  getRolePermissionRowsForPermission(permissionId) {
    const rolePermissions = this.context.rolePermissionsIndexed
      ? Object.values(this.context.rolePermissionsIndexed)
      : [];
    return rolePermissions.filter(
      (rolePermission) => rolePermission.permissionId === permissionId,
    );
  }

  saveRolePermissionChanges(permissionId) {
    const originalRolePermissionRows =
      this.state.originalRolePermissionRows || [];
    const selectedRoleIds = [...new Set(this.state.selectedRoleIds || [])];
    const selectedRoleIdsSet = new Set(
      selectedRoleIds.map((roleId) => String(roleId)),
    );
    const originalRoleIdsSet = new Set(
      originalRolePermissionRows.map((rolePermission) =>
        String(rolePermission.roleId),
      ),
    );

    const roleIdsToAdd = selectedRoleIds.filter(
      (roleId) => !originalRoleIdsSet.has(String(roleId)),
    );
    const rolePermissionsToRemove = originalRolePermissionRows.filter(
      (rolePermission) =>
        !selectedRoleIdsSet.has(String(rolePermission.roleId)),
    );

    const addPromises = roleIdsToAdd.map((roleId) =>
      api
        .send("POST", "/rolePermission", {
          roleId: roleId,
          permissionId: permissionId,
        })
        .then((createdRolePermission) => {
          this.context.updateIndexed(
            "rolePermissionsIndexed",
            createdRolePermission,
          );
          return createdRolePermission;
        }),
    );

    const removePromises = rolePermissionsToRemove.map((rolePermission) =>
      api.send("DELETE", `/rolePermission/${rolePermission.id}`).then(() => {
        this.context.deleteIndexed("rolePermissionsIndexed", rolePermission.id);
        return rolePermission;
      }),
    );

    return Promise.all([...addPromises, ...removePromises]).then((results) => {
      const createdRolePermissions = results.slice(0, addPromises.length);
      const retainedRolePermissions = originalRolePermissionRows.filter(
        (rolePermission) =>
          selectedRoleIdsSet.has(String(rolePermission.roleId)),
      );
      return retainedRolePermissions.concat(createdRolePermissions);
    });
  }

  async createPermission() {
    this.setState({
      isProcessing: true,
    });

    return api
      .send("POST", "/permission", this.state.permission)
      .then((permission) => {
        this.context.updateIndexed("permissionsIndexed", permission);
        this.setState({
          permission: permission,
        });
        return this.saveRolePermissionChanges(permission.id).then(
          (nextRolePermissionRows) => ({
            permission,
            nextRolePermissionRows,
          }),
        );
      })
      .then(({ nextRolePermissionRows }) => {
        this.setState({
          originalRolePermissionRows: JSON.parse(
            JSON.stringify(nextRolePermissionRows),
          ),
          selectedRoleIds: nextRolePermissionRows.map(
            (rolePermission) => rolePermission.roleId,
          ),
        });
        this.props.history.goBack();
        NotistackEnqueueSnackbar("Permission created successfully!", {
          variant: "success",
        });
      })
      .catch((err) => {
        console.log(err);
        NotistackEnqueueSnackbar(
          "Oh no! There was an error creating a new Permission.",
          { variant: "error" },
        );
        this.setState({
          isProcessing: false,
        });
      });
  }

  async updatePermission() {
    this.setState({
      isProcessing: true,
    });

    return api
      .send("PUT", "/permission", this.state.permission)
      .then((permission) => {
        this.context.updateIndexed("permissionsIndexed", permission);
        return this.saveRolePermissionChanges(permission.id).then(
          (nextRolePermissionRows) => ({
            permission,
            nextRolePermissionRows,
          }),
        );
      })
      .then(({ nextRolePermissionRows }) => {
        this.setState({
          originalRolePermissionRows: JSON.parse(
            JSON.stringify(nextRolePermissionRows),
          ),
          selectedRoleIds: nextRolePermissionRows.map(
            (rolePermission) => rolePermission.roleId,
          ),
        });
        this.props.history.goBack();
        NotistackEnqueueSnackbar("Permission updated successfully!", {
          variant: "success",
        });
      })
      .catch((err) => {
        console.log(err);
        NotistackEnqueueSnackbar(
          "Oh no! There was an error updating the Permission.",
          { variant: "error" },
        );
        this.setState({
          isProcessing: false,
        });
      });
  }

  async deletePermission() {
    this.setState({
      isProcessing: true,
    });

    return api
      .send("DELETE", `/permission/${this.state.permission.id}`)
      .then(() => {
        this.context.deleteIndexed(
          "permissionsIndexed",
          this.state.permission.id,
        );
        this.props.history.goBack();
        NotistackEnqueueSnackbar("Permission deleted successfully!", {
          variant: "success",
        });
      })
      .catch((err) => {
        console.log(err);
        NotistackEnqueueSnackbar(
          "Oh no! There was an error deleting the Permission.",
          { variant: "error" },
        );
        this.setState({
          confirmDialogOpen: false,
          isProcessing: false,
        });
      });
  }

  renderContent() {
    const handleChange = (permission) => {
      this.setState({
        permission: {
          ...this.state.permission,
          ...permission,
        },
      });
    };

    return (
      <>
        <CardAdminPermissionGeneral
          permission={this.state.permission}
          onChange={handleChange.bind(this)}
          validationIssues={this.state.validationIssues}
        />
        <CardAdminPermissionRoles
          selectedRoleIds={this.state.selectedRoleIds}
          onChange={(nextSelectedRoleIds) => {
            this.setState({
              selectedRoleIds: nextSelectedRoleIds,
            });
          }}
        />
        <DialogConfirm
          open={this.state.confirmDialogOpen}
          onClose={() => this.setState({ confirmDialogOpen: false })}
          onConfirm={() => this.deletePermission()}
          text="Are you sure you want to delete this Permission? This cannot be undone."
          header="Delete Permission"
          loading={this.state.isProcessing}
        />
      </>
    );
  }

  renderHeader() {
    let title;
    let subtitle;
    const buttonText = this.state.isUpdate ? "Save" : "Create";

    if (!this.state.isUpdate) {
      title = "Create Permission";
      subtitle = null;
    } else {
      title = "Edit Permission";
      subtitle =
        this.state.permission &&
        Permission.getDisplayName(this.state.permission) +
          " • " +
          this.state.permission.id;
    }

    const handleClickBack = () => {
      this.props.history.goBack();
    };

    const handleClickSave = () => {
      const validationIssues = Validator.validate(this.state.permission, {
        name: {
          required: true,
        },
      });

      this.setState({
        validationIssues: validationIssues,
      });

      if (Object.keys(validationIssues).length > 0) {
        return NotistackEnqueueSnackbar(
          "Please correct the displayed issues.",
          { variant: "error" },
        );
      }

      if (this.state.isUpdate) {
        this.updatePermission();
      } else {
        this.createPermission();
      }
    };

    const handleDelete = () => {
      this.setState({
        confirmDialogOpen: true,
      });
    };

    const buttons = [
      <MUIButton onClick={handleClickBack}>Back</MUIButton>,
      <MUILoadingButton
        loading={this.state.isProcessing}
        loadingPosition="start"
        onClick={handleClickSave}
        variant="contained"
        startIcon={<MUISaveButton />}
      >
        {buttonText}
      </MUILoadingButton>,
    ];

    if (this.state.isUpdate) {
      const menuItems = [
        <MUIMenuItem onClick={handleDelete}>
          <MUIListItemIcon>
            <MUIDeleteIcon fontSize="small" />
          </MUIListItemIcon>
          Delete
        </MUIMenuItem>,
      ];
      buttons.push(<Menu menuItems={menuItems} />);
    }

    return <PageHeader title={title} subtitle={subtitle} buttons={buttons} />;
  }
}
